import _ from "underscore";
import ColorLib from "tinycolor2";
import { ApiPipeline } from "avvir";

import { ReduxStore } from "../reducers/root_reducer";
import { InspectionMode } from "../../models/domain/enums/inspection_modes";
import { defaultSelection } from "../reducers/reduce_selection";
import { defaultFilter } from "../reducers/filter/reduce_filter";
import { CustomBimColors, defaultBimColors } from "./bim_colors";

const PROCESSES_KEY = "runningProcesses";
const PHOTO_VISITED_KEY = "photoLocationsVisited";
const DISMISSED_PIPELINE_IDS_KEY = "dismissedPipelineIds";
const CUSTOM_BIM_COLORS_KEY = "customBimColors";

export function readStore(): ReduxStore {
  try {
    const serializedState = localStorage.getItem("store");
    if (serializedState == null) {
      return;
    } else {
      const store = JSON.parse(serializedState);
      if (!Object.values(InspectionMode).includes(store?.selection?.inspectionMode)) {
        store.selection = { ...defaultSelection };
      }
      store.filter = {
        ...defaultFilter,
        onlyShowDeviationsFromCurrentScan: store.selection.inspectionMode === InspectionMode.inspect,
      };
      return store;
    }
  } catch (error) {
    return;
  }
}

export function writeStore(state: Partial<ReduxStore>) {
  try {
    const serializedState = JSON.stringify(state);
    localStorage.setItem("store", serializedState);
  } catch {
    // ignore write errors
  }
}

export function writePipelines(pipelines: ApiPipeline[]): void {
  window.localStorage.setItem(PROCESSES_KEY, JSON.stringify(pipelines));
}

export function removePipelineFromStorage(pipelineId: number): void {
  const pipelines = readPipelines();
  writePipelines(pipelines.filter((existingPipeline) => {
    const validPipeline = existingPipeline.name as string !== "ResponseError" && existingPipeline.id;
    return validPipeline && existingPipeline.id !== pipelineId;
  }));
}

export function writePipeline(pipeline: ApiPipeline): void {
  const pipelines = readPipelines();
  const pipelineIndex = _(pipelines).findIndex({ id: pipeline.id });
  if (pipelineIndex !== -1) {
    pipelines[pipelineIndex] = pipeline;
    writePipelines(pipelines);
  } else {
    pipelines.push(pipeline);
    writePipelines(pipelines);
  }
}

export function readPipelines(): ApiPipeline[] {
  const fromStore = window.localStorage.getItem(PROCESSES_KEY);
  return ((fromStore == null || fromStore === "") ? [] : JSON.parse(fromStore))
    .map((stored) => { return new ApiPipeline(stored); });
}

function readBooleanMap(key: string): {
  [id: number]: boolean
} {
  const fromStore = window.localStorage.getItem(key);
  return ((fromStore == null || fromStore === "") ? {} : JSON.parse(fromStore));
}

function writeInBooleanMapById(key: string, id: number, value: boolean): void {
  const fromStore = readBooleanMap(key);
  fromStore[id] = value;
  window.localStorage.setItem(key, JSON.stringify(fromStore));
}

export function writePhotoLocationVisited(photoLocationId: number): void {
  writeInBooleanMapById(PHOTO_VISITED_KEY, photoLocationId, true);
}

export function readPhotoLocationsVisited(): {
  [id: number]: boolean
} {
  return readBooleanMap(PHOTO_VISITED_KEY);
}

export function readDismissedPipelineIds(): { [id: number]: boolean } {
  return readBooleanMap(DISMISSED_PIPELINE_IDS_KEY);
}

export function writeDismissedPipelineId(pipelineId): void {
  writeInBooleanMapById(DISMISSED_PIPELINE_IDS_KEY, pipelineId, true);
}

export function readCustomBimColors(): CustomBimColors {
  const fromStore = window.localStorage.getItem(CUSTOM_BIM_COLORS_KEY);
  if (fromStore == null) {
    return defaultBimColors();
  }

  return _.mapObject(JSON.parse(fromStore), (color: string) => {
    return new ColorLib(color);
  }) as unknown as CustomBimColors;
}

export function writeCustomBimColors(customBimColors: CustomBimColors) {
  window.localStorage.setItem(CUSTOM_BIM_COLORS_KEY, JSON.stringify(_.mapObject(customBimColors, (color: ColorLib.Instance) => color.toHexString())));
}
